import type { FieldState } from './fieldState';

/**
 * 圃場イベントタイプ
 */
export type FieldEventType =
  | 'SEEDING'
  | 'EMERGENCE'
  | 'PEST_CONTROL'
  | 'HERBICIDE'
  | 'CULTIVATION'
  | 'HARVEST'
  | 'TRANSPORT';

/**
 * イベントタイプ定数
 * ハードコードされた文字列の代わりに使用
 */
export const FieldEventTypes = {
  SEEDING: 'SEEDING',
  EMERGENCE: 'EMERGENCE',
  PEST_CONTROL: 'PEST_CONTROL',
  HERBICIDE: 'HERBICIDE',
  CULTIVATION: 'CULTIVATION',
  HARVEST: 'HARVEST',
  TRANSPORT: 'TRANSPORT',
} as const satisfies Record<string, FieldEventType>;

/**
 * イベント記録タイプ
 * - SCHEDULED: 予定
 * - ACTUAL: 実績
 *
 * ステート算出はバックエンド側の calculateState で行う。
 * - ステート算出には ACTUAL（実績）のみを使用
 * - SCHEDULED（予定）はUI表示用であり、ステートに影響しない
 * - 収穫期(HARVESTABLE): 播種済みの圃場は収穫期遷移基準日（デフォルト 9/15）以降で強制的に遷移する
 *   （防除・収穫予定日/実施日の登録有無に依らず「時期」で入る）
 */
export type FieldRecordType = 'SCHEDULED' | 'ACTUAL';

/**
 * 記録タイプ定数
 */
export const FieldRecordTypes = {
  SCHEDULED: 'SCHEDULED',
  ACTUAL: 'ACTUAL',
} as const satisfies Record<string, FieldRecordType>;

/**
 * 防除の最大回数
 */
export const MAX_PEST_CONTROL_COUNT = 8;

/**
 * 圃場イベント
 */
export interface FieldEvent {
  id: string;
  fieldId: string;
  eventType: FieldEventType;
  eventDate: Date;
  recordType: FieldRecordType;
  /**
   * 連番（1 から始まる）
   * 複数回実施するイベント（PEST_CONTROL等）は 1, 2, 3...。
   * 単発イベント（SEEDING, EMERGENCE, HARVEST）は常に 1。
   * バックエンドは NOT NULL DEFAULT 1 で保持する。
   */
  sequenceNumber: number;
  note: string | null;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * FieldEvent APIレスポンス
 */
export interface FieldEventsResponse {
  fieldId: string;
  state: FieldState;
  events: FieldEvent[];
}

/**
 * イベントタイプの日本語ラベル
 */
export const FIELD_EVENT_TYPE_LABELS: Record<FieldEventType, string> = {
  SEEDING: '播種',
  EMERGENCE: '出芽',
  PEST_CONTROL: '防除',
  HERBICIDE: '除草剤散布',
  CULTIVATION: 'カルチ',
  HARVEST: '収穫',
  TRANSPORT: '輸送',
};

/**
 * 記録タイプの日本語ラベル
 */
export const FIELD_RECORD_TYPE_LABELS: Record<FieldRecordType, string> = {
  SCHEDULED: '予定',
  ACTUAL: '実績',
};

/**
 * イベント追加時の入力型
 */
export interface NewFieldEventInput {
  eventType: FieldEventType;
  eventDate: Date;
  recordType: FieldRecordType;
  /** 連番（PEST_CONTROL等で使用） */
  sequenceNumber?: number;
  note?: string;
}
